import React from 'react';
import { propOr } from 'ramda';
import { PSA_SUPPORTED_LANGUAGES_KEY, RTL_LANG } from '../constants/i18n';
import { determineLocale } from '../utils/determineLocale';
import { useHistory } from 'react-router';
import Card from '@veneer/core/dist/scripts/card';
import Button from '@veneer/core/dist/scripts/button';
import { useTheme } from '@veneer/theme';

import {
  Container,
  ContentContainer,
  ButtonsContainer
} from '../styles/enableHPAIForPrint';
import { hpxTokens } from '../components/ThemeProvider';

const EnableHpAIForPrint = ({
  handleClick,
  declineAllClick,
  csData = {},
  alpacaData,
  loading
}) => {
  const title = propOr('', 'title', csData);
  const agreeButton = propOr('', 'acceptAllButton', csData);
  const declineAllButton = propOr('', 'declineAllButton', csData);
  const body = propOr('', 'body', csData);
  const secondaryBodyText = propOr('', 'secondaryBodyText', csData);
  const supplementalText = propOr('', 'supplementalText', csData);
  const history = useHistory();
  const { language } = determineLocale(PSA_SUPPORTED_LANGUAGES_KEY);
  const { pathname } = history.location;
  const isRTL = RTL_LANG.includes(language) ? true : false;
  const userConsents = pathname.includes('user-consents');
  const theme = useTheme();

  const getButtonContainer = () => {
    return (
      <>
        <Button
          onClick={declineAllClick}
          appearance="secondary"
          loading={loading}
          disabled={loading}
          size="medium"
          id="consent-decline-all-button"
        >
          {!loading && declineAllButton}
        </Button>

        <Button
          onClick={handleClick}
          appearance="secondary"
          loading={loading}
          disabled={loading}
          size="medium"
          id="consent-agree-button"
        >
          {!loading && agreeButton}
        </Button>
      </>
    );
  };

  const cardContent = () => {
    const splitByPTag = (text) => {
      if (!text) return [];

      const matches = text.match(/<p>(.*?)<\/p>/g);
      return matches ? matches.map((p) => p.replace(/<\/?p>/g, '').trim()).filter(Boolean) : [];
    };

    const result = splitByPTag(secondaryBodyText);

    let productSupport = '';
    let basic = '';

    if (alpacaData && typeof alpacaData === 'object') {
      Object.values(alpacaData).forEach((value) => {
        if (value?.purposeId?.includes('prodsupport')) {
          productSupport = value.verbiageValue || '';
        }
        if (value?.purposeId?.includes('basicfunct')) {
          basic = value.verbiageValue || '';
        }
      });
    }

    // Insert verbiage at specific positions if available
    if (basic) result.splice(4, 0, basic);
    if (productSupport) result.splice(7, 0, productSupport);

    const paragraph1 = result.slice(0, 3).join(' ');
    const paragraph2 = result.slice(3, 6).join(' ');
    const paragraph3 = result.slice(6, 9).join(' ');

    return (
      <>
        {body && alpacaData !== null && (
          <>
            <div dangerouslySetInnerHTML={{ __html: body }} />
            <p dangerouslySetInnerHTML={{ __html: paragraph1 }} isRTL={isRTL} />
            <p dangerouslySetInnerHTML={{ __html: paragraph2 }} isRTL={isRTL} />
            <p dangerouslySetInnerHTML={{ __html: paragraph3 }} isRTL={isRTL} />
            <p dangerouslySetInnerHTML={{ __html: supplementalText }} />
          </>
        )}
        <ButtonsContainer>
          {alpacaData !== null && userConsents && getButtonContainer()}
        </ButtonsContainer>
      </>
    );
  };

  const getWelcomeContainer = () => {
    const backgroundColor = hpxTokens.manatee3;

    return (
      <Container className={'body-regular'} color={backgroundColor} isRTL={isRTL}>
        {body && alpacaData !== null && title && (
          <ContentContainer>
            <h3>{title}</h3>
            <Card
              id="settings-container"
              data-testid="privacy-settings"
              className="privacy-settings-card"
              customStyle={{
                borderRadius: '7px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
              content={cardContent()}
            />
          </ContentContainer>
        )}
      </Container>
    );
  };
  return getWelcomeContainer();
};

export default EnableHpAIForPrint;
